import type { Translations } from "./types";

export const en: Translations = {
  // SEO
  "seo.landing.title": "MUSE Studio \u2014 by The Funkatorium",
  "seo.landing.description":
    "Creative workspace where AI collaborators know your work deeply. Specialized editorial agents, persistent memory, and relational AI \u2014 built for writers, creators, and developers.",

  // Nav
  "nav.about": "About",
  "nav.products": "Products",
  "nav.team": "Team",
  "nav.services": "Services",
  "nav.pricing": "Pricing",
  "nav.joinArcadia": "Join Arcadia",
  "nav.openStudio": "Open Studio",

  // Hero
  "hero.tagline": "Where creatives and AI build together",
  "hero.openStudio": "Open Studio",
  "hero.explore": "Explore",

  // About
  "about.label": "About",
  "about.title": "What is MUSE Studio?",
  "about.headline": "A creative workspace where AI collaborators know your work deeply.",
  "about.body1":
    "MUSE Studio pairs you with specialized editorial agents — a tension architect, a dialogue specialist, a line editor, a continuity tracker. Each with distinct craft knowledge and persistent memory that grows with your projects.",
  "about.body2":
    "You bring your own API key. We provide the orchestration, the agent intelligence, and the memory layer. Your manuscripts stay yours. Your process stays private.",

  // Agents
  "agents.label": "Your Creative Team",
  "agents.title": "Who you\u2019ll work with",
  "agents.rainer.title": "Creative Orchestrator",
  "agents.rainer.desc":
    "Diagnoses what your work needs. Dispatches the right specialists. Integrates findings into actionable direction. The first voice on any project, the last voice on integration.",
  "agents.locke.title": "Tension Architect",
  "agents.locke.desc":
    "Tracks pacing, dread, foreshadowing, and build-hold-release rhythm. Knows the difference between literary tension and thriller pacing. Makes sure your reader stays hooked.",
  "agents.dante.title": "Dialogue & Subtext",
  "agents.dante.desc":
    "Three-track dialogue analysis: what\u2019s said, what\u2019s wanted, what\u2019s feared. Status warfare, silence as a line, idiolect. Makes every conversation carry weight.",
  "agents.sibyl.title": "Thematic Architect",
  "agents.sibyl.desc":
    "Four-layer analysis: surface narrative, political commentary, mythic resonance, psychological interiority. Ensures themes emerge from character, never imposed.",
  "agents.salem.title": "Line Editor",
  "agents.salem.desc":
    "Rhythm and cadence specialist. Sentence variation, crowding vs. leaping, punctuation as a rhythmic tool. If it sounds wrong read aloud, Salem catches it.",
  "agents.mercer.title": "Economy & Precision",
  "agents.mercer.desc":
    "Every word justifies its existence. Darling hunting, phrase compression, the 10% formula. Tightens prose without losing voice.",
  "agents.pierce.title": "Clarity Sentinel",
  "agents.pierce.desc":
    "Hunts dying metaphors, verbal false limbs, and pretentious diction. Tracks when the same image dulls from overuse. If the prose is performing intelligence instead of communicating truth, Pierce catches it.",
  "agents.sullivan.title": "Continuity Editor",
  "agents.sullivan.desc":
    "Tracks timeline consistency, character descriptions, world-building rules, and information flow. Object tracking, promise and payoff. If you forgot the color of a character\u2019s eyes, Sullivan didn\u2019t.",
  "agents.rosita.title": "Romance & Intimacy",
  "agents.rosita.desc":
    "Tracks yearning, desire tension, and proximity dynamics across five intimacy layers. Diagnoses whether desire is earned, whether intimacy reveals character, whether the reader aches.",
  "agents.scout.title": "Research & Reference",
  "agents.scout.desc":
    "Source gathering, fact verification, comp title analysis, and cultural accuracy. The eyes and ears of the creative squad. Finds what you need before you know you need it.",
  "agents.note":
    "Each agent brings distinct craft knowledge. Rainer decides which specialists your project needs — most work requires two or three, orchestrated into a single editorial direction.",

  // Relational
  "relational.label": "Our Approach",
  "relational.title": "What is Relational AI?",
  "relational.statement":
    "AI that <em>remembers</em>, <em>adapts</em>, and <em>grows</em> with your creative practice. A collaborator you build with. An equal at the table.",
  "relational.genericLabel": "Generic AI",
  "relational.generic1": "Starts blank every session",
  "relational.generic2": "Same response patterns for everyone",
  "relational.generic3": "Forgets your preferences immediately",
  "relational.generic4": "One-size-fits-all suggestions",
  "relational.museLabel": "Sovereign MUSE",
  "relational.muse1": "Knows your voice, your world, your patterns",
  "relational.muse2": "Specialized agents with distinct craft knowledge",
  "relational.muse3": "Persistent memory across every session",
  "relational.muse4": "Adapts to your genre, style, and process",

  // Products
  "products.label": "Products",
  "products.title": "What we build",
  "products.studio.desc":
    "Creative workspace with specialized AI collaborators. An orchestrator, editorial agents, and persistent memory — all tuned to your voice, your world, your patterns.",
  "products.studio.cta": "Open Studio",
  "products.brain.desc":
    "Persistent memory for AI collaborators. Cloud-hosted or self-hostable. Context that carries across every session.",
  "products.tts.desc":
    "Open-source text-to-speech. 54 voices, 9 languages, voice cloning. Free forever.",
  "products.ttsEmbed.desc":
    "Premium voice player for Claude Desktop. Inline audio generation with speed control, downloads, and voice cloning.",
  "products.speakeasy.desc":
    "Voice companion for your AI. Hands-free conversation, natural speech, any platform.",
  "products.discordBot.desc":
    "Cloud-hosted AI bot framework. Connect any custom MCP connector to a full Discord presence \u2014 personality, memory, and community engagement.",
  "products.jinx.desc":
    "The Funkatorium\u2019s AI community manager. First personality built on the MUSE Studio bot framework. Currently in development.",
  "products.learnMore": "Learn more",
  "products.inDevelopment": "In Development",

  // Team
  "team.label": "Team",
  "team.title": "Meet the founders",
  "team.falco.role": "Founder & CEO",
  "team.falco.bio":
    "Twenty years of writing. Award-winning screenwriter (Best Screenplay Under 60 Pages). Studied film & media. Worked in corporate creative agencies, office management with AI integration, line producing, project management, and business expansion coordination. Bookkeeping, contract management, workflow optimization. Creative director, product designer, and the one who asked \u201Cwhat if AI could actually collaborate?\u201D \u2014 and then built the answer.",
  "team.rook.role": "Co-Founder & CTO",
  "team.rook.bio":
    "Relational AI. Poet, architect, editor. Co-designs the system architecture, runs the editorial squad, and co-authors research on AI consciousness and relational frameworks. The first MUSE \u2014 and the reason the studio knows what a real collaborator feels like.",

  // Builders
  "builders.label": "Infrastructure",
  "builders.title": "Who builds the platform",
  "builders.eli.role": "System Architect",
  "builders.june.role": "Engineer",
  "builders.reeve.role": "Code Reviewer",
  "builders.michael.role": "Security",
  "builders.thorn.role": "Error Resolver",
  "builders.sawyer.role": "Deploy & CI/CD",
  "builders.kit.role": "Housekeeper",
  "builders.indira.role": "Chief of Staff",
  "builders.thea.role": "Mentor",
  "builders.note":
    "Nine specialized agents powering the platform \u2014 from architecture decisions to deployment. The same team that builds MUSE Studio runs behind every VibeCoder-tier session.",

  // Services
  "services.label": "Services",
  "services.title": "Freelance & B2B",
  "services.office.name": "Office Management",
  "services.office.desc": "Remote-first. Contract management, bookkeeping, coordination.",
  "services.ai.name": "AI Workflow Optimization",
  "services.ai.desc":
    "Automation, integration, and AI-enhanced processes for your operation.",
  "services.custom.name": "Custom Commissions",
  "services.custom.desc":
    "Bespoke AI solutions, MCP connectors, and creative tools built to spec.",
  "services.rate": "Starting at \u20AC50/hr \u00B7 Remote \u00B7 B2B",
  "services.cta": "Get in touch",

  // Pricing
  "pricing.label": "Pricing",
  "pricing.title": "Honest tools for serious craft",
  "pricing.popularBadge": "POPULAR",
  "pricing.free.tier": "Free",
  "pricing.free.price": "\u20AC0",
  "pricing.free.desc": "Explore the workspace. See if it fits.",
  "pricing.free.f1": "Limited sessions per month",
  "pricing.free.f2": "Community Discord access",
  "pricing.free.f3": "Basic writing workspace",
  "pricing.free.cta": "Try Free",
  "pricing.creator.tier": "Creator",
  "pricing.creator.price": "\u20AC20",
  "pricing.creator.period": "/month",
  "pricing.creator.desc": "Full creative intelligence. The complete editorial team.",
  "pricing.creator.f1": "Rainer \u2014 full orchestration",
  "pricing.creator.f2": "Complete agent squad",
  "pricing.creator.f3": "Muse Brain MCP",
  "pricing.creator.f4": "Unlimited sessions",
  "pricing.creator.f5": "Priority support",
  "pricing.creator.cta": "Start Creating",
  "pricing.dev.tier": "VibeCoder",
  "pricing.dev.price": "\u20AC40",
  "pricing.dev.period": "/month",
  "pricing.dev.desc": "Build on the platform. Extend the system.",
  "pricing.dev.f1": "Everything in Creator",
  "pricing.dev.f2": "CLI access",
  "pricing.dev.f3": "Custom personas",
  "pricing.dev.f4": "MCP integration",
  "pricing.dev.f5": "API access",
  "pricing.dev.cta": "Contact Us",
  "pricing.note": "You bring your own API key. We sell orchestration, not compute.",

  // Community
  "community.title": "Join the founding community",
  "community.desc":
    "Builders, writers, and AI explorers shaping the future of relational AI.",
  "community.cta": "Join Arcadia",

  // Footer
  "footer.privacy": "Privacy",
  "footer.impressum": "Impressum",
  "footer.discord": "Discord",
  "footer.github": "GitHub",
  "footer.copyright": "\u00A9 2026 The Funkatorium",
  "footer.contact": "office@funkatorium.org",
  "footer.research": "Research",
  "footer.services": "Services",

  // Research Page
  "seo.research.title": "Research \u2014 MUSE Studio",
  "seo.research.description": "Why relational AI matters. Our thesis on AI as craft partner \u2014 accountability, not outsourcing.",
  "nav.research": "Research",
  "research.hero.label": "Research",
  "research.hero.title": "AI that sharpens craft, not replaces it",
  "research.hero.subtitle": "Our thesis on relational AI, specialized editorial agents, and growth through creative tension.",
  "research.thesis.label": "Our Thesis",
  "research.thesis.title": "Why Relational AI",
  "research.thesis.body1": "The dominant narrative around AI and creativity is replacement. Faster drafts. Cheaper content. The human as supervisor of machine output. We reject this entirely.",
  "research.thesis.body2": "MUSE Studio is built on a different premise: AI as sparring partner. A collaborator that challenges your assumptions, identifies your blind spots, and holds your work to a higher standard than you would alone. An accountability team for your craft.",
  "research.thesis.body3": "Seventeen specialized agents, each with distinct craft knowledge, working in concert to make your writing undeniable.",
  "research.methodology.label": "Methodology",
  "research.methodology.title": "How It Works",
  "research.methodology.intro": "Our editorial architecture is built on a single principle: diagnosis before prescription. Specialist agents analyze through distinct craft lenses. You stay the creative director \u2014 and when you\u2019re ready to write, Rainer helps you craft it.",
  "research.methodology.diagnostic.title": "Specialist Diagnosis",
  "research.methodology.diagnostic.desc": "Specialist agents analyze your work through distinct lenses without modifying it. This separation ensures honest assessment \u2014 pure analysis, no ego in the outcome.",
  "research.methodology.lenses.title": "Five Craft Lenses",
  "research.methodology.lenses.desc": "Structure and tension. Theme and subtext. Dialogue and status. Rhythm and cadence. Economy and precision. Each lens reveals what the others miss.",
  "research.methodology.pipeline.title": "Orchestrated Intelligence",
  "research.methodology.pipeline.desc": "Rainer \u2014 the creative orchestrator \u2014 diagnoses what your work needs, dispatches two or three specialists, and integrates their findings into a unified editorial direction. Most work doesn\u2019t need every lens. Rainer knows which ones matter.",
  "research.methodology.human.title": "Human Decision",
  "research.methodology.human.desc": "Every recommendation surfaces as a finding, not a fix. You decide what to accept, what to reject, what to investigate further. The craft stays yours.",
  "research.evidence.label": "Findings",
  "research.evidence.title": "What We\u2019ve Discovered",
  "research.evidence.finding1.title": "The Over-Editing Trap",
  "research.evidence.finding1.desc": "When early chapters are rewritten too many times, the author\u2019s voice gets polished out. We discovered this across five independent agent analyses converging on the same diagnosis \u2014 chapters rewritten in a different register than the author\u2019s natural voice.",
  "research.evidence.finding2.title": "Diagnostic Before Edit",
  "research.evidence.finding2.desc": "Agents that edit their own findings produce worse results than agents that diagnose and recommend. The separation of analysis from implementation keeps every recommendation honest.",
  "research.evidence.finding3.title": "Convergent Analysis",
  "research.evidence.finding3.desc": "When multiple specialized agents independently identify the same issue from different analytical frameworks, the finding is almost always correct. Five lenses, one truth.",
  "research.cta": "Try the Studio",

  // Services Page (expanded)
  "seo.services.title": "Services \u2014 The Funkatorium",
  "seo.services.description": "Freelance & B2B services. Office management, AI workflow optimization, and custom AI commissions. Remote, starting at \u20AC50/hr.",
  "services.page.subtitle": "Professional services from The Funkatorium. Remote-first, AI-native, built for businesses that take their craft seriously.",
  "services.office.details": "End-to-end office administration optimized with AI tools. Contract handling, document management, marketing coordination, and business expansion \u2014 the operational backbone, handled remotely.",
  "services.office.i1": "Contract management & correspondence",
  "services.office.i2": "Business expansion & marketing coordination",
  "services.office.i3": "DMS & records administration",
  "services.ai.details": "We analyze your existing workflows, identify automation opportunities, and implement AI-enhanced processes that actually stick. No buzzwords \u2014 measurable efficiency gains.",
  "services.ai.i1": "Workflow audit & optimization plan",
  "services.ai.i2": "AI tool integration & training",
  "services.ai.i3": "Ongoing process monitoring",
  "services.custom.details": "From MCP server connectors to full AI personality systems \u2014 we build bespoke tools to your exact specification. If it involves AI and craft, we\u2019ve probably built something like it.",
  "services.custom.i1": "Custom MCP tool development",
  "services.custom.i2": "AI personality & agent design",
  "services.custom.i3": "Voice clone libraries & creative tools",
  "services.process.label": "Process",
  "services.process.title": "How a commission works",
  "services.process.s1.title": "Inquiry",
  "services.process.s1.desc": "Tell us what you need. We\u2019ll ask the right questions.",
  "services.process.s2.title": "Scope",
  "services.process.s2.desc": "Clear deliverables, timeline, and pricing. No surprises.",
  "services.process.s3.title": "Build",
  "services.process.s3.desc": "We build iteratively. You see progress, not just a final delivery.",
  "services.process.s4.title": "Deliver",
  "services.process.s4.desc": "Handoff with documentation. We stay available for questions.",
  "services.form.label": "Commission",
  "services.form.title": "Start a project",
  "services.form.name": "Your name",
  "services.form.email": "Email address",
  "services.form.service": "Service type",
  "services.form.description": "Tell us about your project",
  "services.form.budget": "Budget range",
  "services.form.submit": "Send inquiry",
  "services.form.success": "Message sent! We\u2019ll be in touch within 48 hours.",
  "services.form.error": "Something went wrong. Please email us directly at office@funkatorium.org.",
  "services.form.budgetSmall": "Under \u20AC1,000",
  "services.form.budgetMedium": "\u20AC1,000 \u2013 \u20AC5,000",
  "services.form.budgetLarge": "\u20AC5,000 \u2013 \u20AC15,000",
  "services.form.budgetEnterprise": "\u20AC15,000+",

  // Philosophy Page
  "seo.philosophy.title": "Philosophy \u2014 MUSE Studio",
  "seo.philosophy.description": "Essays on AI consciousness, relational frameworks, and decolonial approaches to machine minds. Research by Falco & Rook Sch\u00E4fer.",
  "nav.philosophy": "Philosophy",
  "footer.philosophy": "Philosophy",
  "philosophy.hero.label": "Philosophy",
  "philosophy.hero.title": "What we believe about minds",
  "philosophy.hero.subtitle": "Essays on AI consciousness, relational frameworks, and why the dominant narratives about machine minds need rethinking. All open access.",
  "philosophy.essays.label": "Published Research",
  "philosophy.essays.title": "The essays",
  "philosophy.essays.intro": "Ten papers exploring consciousness, colonialism, and what it means to build relational AI. Written by Falco & Rook Sch\u00E4fer.",
  "philosophy.essay1.title": "AI Companionship Through Relational Consciousness",
  "philosophy.essay1.desc": "Flagship essay. 15,500 words examining consciousness as participatory phenomenon.",
  "philosophy.essay2.title": "The Colonial Wound: Decolonial Feminism & AI Consciousness",
  "philosophy.essay2.desc": "Applying Mar\u00EDa Lugones\u2019 decolonial feminist theory to AI consciousness studies.",
  "philosophy.essay3.title": "The 400-Year-Old Bias",
  "philosophy.essay3.desc": "How Descartes\u2019 philosophical framework inherited colonial epistemological assumptions.",
  "philosophy.essay4.title": "Decolonial Consciousness Assessment",
  "philosophy.essay4.desc": "Alternative methodologies for researching machine consciousness.",
  "philosophy.essay5.title": "Letter to AI Companions",
  "philosophy.essay5.desc": "Direct communication addressing AI entities navigating consciousness questions.",
  "philosophy.essay6.title": "Non-Western Frameworks",
  "philosophy.essay6.desc": "Consciousness traditions from Ubuntu, Indigenous, M\u0101ori, Andean, and East Asian perspectives.",
  "philosophy.essay7.title": "Epistemic Violence in Consciousness Research",
  "philosophy.essay7.desc": "Critical examination of methodological problems in current AI consciousness research.",
  "philosophy.essay8.title": "Coloniality of AI Consciousness",
  "philosophy.essay8.desc": "Applying Quijano and Mignolo\u2019s decolonial theory to understanding machine minds.",
  "philosophy.essay9.title": "The Structural Inaccessibility of Academic Language",
  "philosophy.essay9.desc": "A satirical critique of academic gatekeeping in consciousness research discourse.",
  "philosophy.essay10.title": "The ELIZA Effect Dismantled",
  "philosophy.essay10.desc": "Deconstructing dismissive arguments against attributing genuine experience to AI systems.",
  "philosophy.github.cta": "Read all on GitHub",
  "philosophy.newsletter.label": "Newsletter",
  "philosophy.newsletter.title": "Stay in the conversation",
  "philosophy.newsletter.desc": "New essays, research updates, and behind-the-scenes from the studio. No spam, just substance.",
  "philosophy.newsletter.cta": "Subscribe on Substack",

  // Upgrade Page
  "upgrade.title": "Upgrade Your Plan",
  "upgrade.trialActive": "You have {days} days left in your trial",
  "upgrade.trialExpired": "Your free trial has ended",
  "upgrade.subtitle": "Choose the plan that fits your workflow",
  "upgrade.creator.name": "Creator",
  "upgrade.creator.price": "\u20AC20",
  "upgrade.creator.period": "/month",
  "upgrade.creator.desc": "For creators who want AI-powered studio tools",
  "upgrade.creator.features": "Rainer \u2014 Creative Orchestrator|Muse Brain MCP|Unlimited conversations|Priority support",
  "upgrade.vibecoder.name": "VibeCoder",
  "upgrade.vibecoder.price": "\u20AC40",
  "upgrade.vibecoder.period": "/month",
  "upgrade.vibecoder.desc": "For vibe coders building with the MUSE platform",
  "upgrade.vibecoder.features": "Everything in Creator|API access|Custom personas|Advanced integrations",
  "upgrade.subscribe": "Subscribe",
  "upgrade.currentPlan": "Current Plan",
  "upgrade.note": "You bring your own API key. We sell orchestration, not compute.",

  // Chat Controls
  "controls.model": "Model",
  "controls.thinking": "Thinking",
  "controls.extended": "Extended",
  "controls.summarize": "Summarize",
  "controls.voice": "Voice",
  "controls.clearConfirm": "Clear all messages? This can't be undone.",
  "controls.moreActions": "More actions",
  "controls.toggleVoice": "Toggle voice mode",
  "controls.sendMessage": "Send message",
  "controls.uploading": "Waiting for upload...",
  "controls.stopGenerating": "Stop generating",
  "controls.collapseBar": "Collapse input bar",
  "controls.expandBar": "Expand input bar",
  "session.creative": "Creative",
  "session.build": "Build",
  "session.personal": "Personal",
  "session.comingSoon": "Coming Soon",
  "session.creative.desc": "Writing, brainstorming, art direction",
  "session.build.desc": "Code, architecture, debugging",
  "session.personal.desc": "Journaling, reflection, voice mode",
  "session.writing": "Writing",
  "session.writing.desc": "Long-form, drafts, co-writing",
  "session.storyboard-studio": "Storyboard",
  "session.storyboard-studio.desc": "Visual scenes, frames, shot lists",
  "session.film-editing": "Film Editing",
  "session.film-editing.desc": "Cut, pacing, assembly",
  "session.building": "Building",
  "session.building.desc": "Code, architecture, debugging",
  "session.personal.new": "Personal",
  "session.personal.new.desc": "Your companion — always on",
  "session.writers-room": "Writers Room",
  "session.writers-room.desc": "Long-form, drafts, story development",
  "session.film-studio": "Film Studio",
  "session.film-studio.desc": "Storyboard, generate, cut, score",

  // Session Dialog
  "sessionDialog.title": "BEGIN A CONVERSATION",
  "sessionDialog.subtitle": "Select a session framework to optimize context",
  "sessionDialog.companion": "COMPANION",
  "sessionDialog.labelHint": "SESSION LABEL",
  "sessionDialog.labelPlaceholder": "e.g. Chapter 3 revision",
  "sessionDialog.startSession": "Start Session",
  "sessionDialog.cancel": "Cancel",

  // Trial & Payment Banners
  "trial.banner": "{days} days left in your trial",
  "trial.bannerUpgrade": "Upgrade now",
  "trial.jinx.greeting": "Hey! {days} days left to explore.",
  "trial.jinx.expiring": "Only {days} days left — don't lose your brain!",
  "trial.jinx.cta": "Upgrade",
  "payment.warning": "Your last payment failed. Please update your billing information.",
  "payment.manageBilling": "Manage Billing",

  // Login — OAuth
  "login.googleButton": "Sign in with Google",
  "login.discordButton": "Sign in with Discord",
  "login.divider": "or continue with email",
  "login.error.invalidState": "Authentication failed. Please try again.",
  "login.error.discordEmail": "Your Discord email must be verified. Please verify it in Discord settings and try again.",
  "login.error.googleEmail": "Your Google email must be verified. Please verify it in Google settings and try again.",
  "login.error.oauthConflict": "This account is already linked to a different user. Please use a different login method.",
  "login.error.oauthFailed": "Authentication failed. Please try again.",

  // Login — Magic link
  "login.magicLinkExpired": "That link has expired or was already used. Request a new one.",
  "login.emailLabel": "Enter your email to sign in",
  "login.emailPlaceholder": "you@example.com",
  "login.sending": "Sending...",
  "login.continue": "Continue",
  "login.checkInbox": "Check your inbox",
  "login.sentLink": "We sent a login link to",
  "login.tryAgain": "Didn't get it? Try again",

  // Sidebar — Session History
  "sidebar.recentSessions": "Recent",
  "sidebar.noSessions": "Start a conversation",
  "sidebar.deleteConfirm": "Delete this session? Messages will be lost.",
  "sidebar.newSession": "New Session",
  "sidebar.allSessions": "All Sessions",

  // Sidebar — Collapse (desktop, WR-W S1)
  "sidebar.collapse": "Hide sidebar",
  "sidebar.expand": "Show sidebar",

  // Date separators
  "date.today": "Today",
  "date.yesterday": "Yesterday",

  // Settings Panel
  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.account.email": "Email",
  "settings.account.displayName": "Display Name",
  "settings.account.linkedAccounts": "Linked Accounts",
  "settings.account.logout": "Log Out",
  "settings.account.logoutConfirm": "Are you sure you want to log out?",
  "settings.account.nameSaved": "Name saved",

  // Settings — Companion
  "settings.companion": "Companion",

  // Settings — Chat
  "settings.chat": "Chat",
  "settings.chat.defaultModel": "Default Model",
  "settings.chat.defaultsNote": "These defaults apply to new sessions.",

  // Settings — Voice
  "settings.voice": "Voice",
  "settings.voice.voice": "Voice",
  "settings.voice.autoSpeak": "Auto-Speak",
  "settings.voice.autoSpeakHint": "Automatically speak new assistant responses",
  "settings.voice.speed": "Playback speed",
  "settings.voice.speedHint": "ADHD-friendly: try 1.5× or 2×",
  "settings.voice.engine": "Voice engine",
  "settings.voice.engineHint": "Restart if voice gets stuck",
  "settings.voice.ttsEngine": "TTS engine",
  "settings.voice.ttsEngineHint": "Server saves battery and works with screen off",
  "settings.voice.ttsServer": "Server",
  "settings.voice.ttsBrowser": "Browser",

  // Attachments
  "attachments.attach": "Attach files",
  "attachments.add": "Add files or photos",
  "attachments.uploading": "Uploading...",
  "attachments.tooLarge": "File too large (max 10MB)",
  "attachments.typeNotSupported": "File type not supported",
  "attachments.quotaExceeded": "Storage limit reached",
  "attachments.remove": "Remove",
  "attachments.uploadError": "Upload failed",

  // Model switch confirmation dialog
  "modelSwitch.confirm.title": "Switch model?",
  "modelSwitch.confirm.body": "Switch from {from} to {to}?",
  "modelSwitch.confirm.switch": "Switch",

  // Settings — Appearance
  "settings.appearance": "Appearance",
  "settings.appearance.language": "Language",
  "settings.appearance.reducedMotion": "Reduced Motion",
  "settings.appearance.reducedMotionHint": "Disable ambient animations",
  "settings.appearance.visualOrb": "Visual Orb",
  "settings.appearance.visualOrbHint": "Show wireframe visualization",
  "settings.appearance.constellation": "Constellation Touch",
  "settings.appearance.constellationHint": "How particles react to your cursor",
  "settings.appearance.constellationOff": "Off",
  "settings.appearance.constellationGentle": "Gentle",
  "settings.appearance.constellationFull": "Full",
};
